import LazyImage from "./ui/LazyImage";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { highlightText } from "../utils/TextHighLighter";
import { useSearchContext } from "../components/Header/SearchContext";

type SiteIdentityProps = {
  imageSrc: string;
  compactImageSrc?: string;
  imageAlt?: string;
  searchQuery?: string;
  className?: string;
  onStickyChange?: (sticky: boolean) => void;
  enableMobileStickyOnScroll?: boolean;
};

export default function SiteIdentity({
  imageSrc,
  compactImageSrc,
  imageAlt = "لوگو",
  searchQuery = "",
  className = "",
  onStickyChange,
  enableMobileStickyOnScroll = true,
}: SiteIdentityProps) {
  const heroRef = useRef<HTMLAnchorElement | null>(null);
  const { query } = useSearchContext(); // 👈 ۲. استخراج مقدار سرچ فعلی از کانکست
  const isSearching = query && query.trim() !== ""; // 👈 ۳. وضعیت فعال بودن سرچ

  useEffect(() => {
    if (!enableMobileStickyOnScroll || isSearching) {
      // 👈 اگر در حال سرچ بود، استیکی غیرفعال شود
      setIsSticky(false);
      return;
    }

    const heroEl = heroRef.current;

    const update = (entry?: IntersectionObserverEntry) => {
      const isMobile = window.innerWidth < 768;
      const sticky = isMobile ? !!entry && !entry.isIntersecting : false;
      setIsSticky(sticky);
    };

    if (!heroEl || typeof IntersectionObserver === "undefined") {
      const fallback = () => {
        setIsSticky(window.innerWidth < 768 && window.scrollY > 400);
      };

      window.addEventListener("scroll", fallback, { passive: true });
      fallback();

      return () => window.removeEventListener("scroll", fallback);
    }

    const observer = new IntersectionObserver(([entry]) => update(entry), {
      threshold: 0,
      rootMargin: "-72px 0px 0px 0px",
    });

    observer.observe(heroEl);

    return () => observer.disconnect();
  }, [enableMobileStickyOnScroll, isSearching]);

  const lastScrollY = useRef(0);
  const scrollUpDistance = useRef(0);
  const [isSticky, setIsSticky] = useState(false);
  const visibleImage = compactImageSrc && isSticky ? compactImageSrc : imageSrc;
  const [showBar, setShowBar] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      const isMobile = window.innerWidth < 768;

      if (!isMobile || isSearching) {
        // 👈 لغو نوارهای اسکرولی موبایل در زمان سرچ
        setIsSticky(false);
        setShowBar(false);
        return;
      }

      const diff = currentY - lastScrollY.current;
      const goingDown = diff > 0;
      const goingUp = diff < 0;

      const shouldBeSticky = currentY > 400;
      setIsSticky(shouldBeSticky);

      if (goingDown && shouldBeSticky) {
        setShowBar(true);
        scrollUpDistance.current = 0;
      }

      if (goingUp) {
        scrollUpDistance.current += Math.abs(diff);

        if (scrollUpDistance.current > 60) {
          setShowBar(false);
        }
      } else {
        scrollUpDistance.current = 0;
      }

      lastScrollY.current = currentY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [isSearching]);

  useEffect(() => {
    onStickyChange?.(isSticky);
  }, [isSticky, onStickyChange]);

  // 👈 ۴. اگر کاربر در حال جستجو است، هیچکدام از آیدنتیتی‌ها (ثابت یا استیکی موبایل) رندر نشوند
  if (isSearching) return null;

  return (
    <>
      {/* آیدنتیتی اصلی و بزرگ صفحه خانه */}
      <Link
        ref={heroRef}
        to="/founder"
        className={`flex flex-col items-center justify-center transition-all duration-300 animate-fade-in ${className}`}
      >
        <LazyImage
          src={imageSrc}
          alt={imageAlt}
          className="size-[335px] object-cover background-home-image"
        />

        <span className="font-kahroba text-center text-accent-04 -mt-7">
          <p className="text-[15px] font-semibold">
            {highlightText("حجة الاسلام و المسلمین", searchQuery)}
          </p>

          <h4 className="text-xl font-black">
            {highlightText("ســید جــواد شهرســتانی", searchQuery)}
          </h4>

          <p className="text-[15px] font-semibold">
            {highlightText(
              "موسس و مدیر موسسه آل البیت علیهم اسلام",
              searchQuery,
            )}
          </p>
        </span>
      </Link>

      {/* آیدنتیتی کوچک استیکی بالای صفحه در موبایل */}
      <Link
        to="/founder"
        className={`
          fixed md:hidden top-0 left-0 right-0 z-50
          bg-green-02/60 backdrop-blur-lg
          flex items-center gap-3 px-3 h-[72px]
          transition-all duration-200 ease-in-out
          ${
            isSticky && showBar
              ? "translate-y-0 opacity-100"
              : "-translate-y-full opacity-0 pointer-events-none"
          }
        `}
      >
        <div className="size-12 overflow-hidden rounded-xl">
          <LazyImage src={visibleImage} alt={imageAlt} />
        </div>

        <div className="text-right text-accent-04">
          <p className="text-[10px] font-normal">حجة الاسلام و المسلمین</p>
          <h4 className="text-xs font-extrabold">ســید جــواد شهرســتانی</h4>
          <h4 className="text-[10px] font-medium">
            موسس و مدیر موسسه آل البیت علیهم اسلام
          </h4>
        </div>
      </Link>
    </>
  );
}
